import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogModalType,
    DialogSurface,
    DialogTitle,
    makeStyles,
} from '@fluentui/react-components';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface IConfirmationDialogV9Props {
    /** When false, the dialog closes and runs proper cleanup (removes aria-hidden, restores focus). Default true. */
    open?: boolean;
    /** Called when the dialog is dismissed via Escape, overlay click, or other light-dismiss. */
    onDismiss?: () => void;
    /** Override the dialog title (e.g. t('saveChanges')). */
    title?: string | null;
    /** Override the dialog body message. */
    content?: string | null;
    /**
     * Caller-owned action buttons (required). Aligns with ModalV9 `buttons`.
     * Put Cancel first so DialogActions margin-auto pushes it left in three-button layouts.
     * Wrap Cancel in DialogTrigger for dismiss/focus cleanup parity.
     */
    buttons: ReactNode;
    /** Fluent dialog modal type. Default `'modal'`. Use `'alert'` for actions-only dismiss. */
    modalType?: DialogModalType;
}

const useDialogActionsStyles = makeStyles({
    root: {
        '> :first-child': { marginRight: 'auto' },
        width: '100%',
        marginTop: '7px',
        marginBottom: '7px',
    },
});

const ConfirmationDialogV9: FC<IConfirmationDialogV9Props> = (props: IConfirmationDialogV9Props) => {
    const { root: autoRightMarginOverride } = useDialogActionsStyles();
    const { t } = useTranslation();
    const open = props.open !== false;
    const modalType = props.modalType ?? 'modal';

    const getTitle = (): string => {
        if (props.title) {
            return props.title;
        }
        return t('saveChanges');
    };

    const getMessage = (): string => {
        if (props.content) {
            return props.content;
        }
        return t('saveChangesConfirmation');
    };

    return (
        <Dialog
            modalType={modalType}
            open={open}
            onOpenChange={(_event, data) => {
                if (!data.open) {
                    if (modalType === 'alert' && data.type === 'escapeKeyDown') {
                        return;
                    }
                    props.onDismiss?.();
                }
            }}
        >
            <DialogSurface>
                <DialogBody role='alert'>
                    <DialogTitle>{getTitle()}</DialogTitle>
                    <DialogContent>{getMessage()}</DialogContent>
                    <br></br>
                </DialogBody>
                <DialogActions className={autoRightMarginOverride}>{props.buttons}</DialogActions>
            </DialogSurface>
        </Dialog>
    );
};
export default ConfirmationDialogV9;
